import { IPendingSaveSearch, PendingActionType } from 'components/authentication/types';
import { BannerType } from 'components/notification-banner/bannersConfig';
import { SavedSearchActiveModal } from 'components/saved-search/shared/active-modal';
import { Route } from 'config/routes';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import { ISaveSearch, MutationType } from 'store/sagas/apiService/types';
import { authGate, AuthGateResponse } from 'store/sagas/routing/handlers/utils';
import {
  deleteSaveSearch,
  saveSearch,
  saveSearchWithValidation,
  setMenuDotVisible,
  setNotificationBannerMessage,
  setSavedSearchActiveModal,
  setToastMessage,
  updateSaveSearch,
  updateSaveSearchFrequency,
} from 'store/state/app/actions';
import { DELETE_SAVE_SEARCH, SAVE_SEARCH, SAVE_SEARCH_WITH_VALIDATION, UPDATE_SAVED_SEARCH, UPDATE_SAVED_SEARCH_FREQUENCY } from 'store/state/app/types';
import { savedSearchesListSelector } from 'store/state/domainData/selectors';
import { mutate } from 'store/state/mutationsResponse/actions';
import { navigateTo } from 'store/state/router/actions';
import { previousSavedSearchIdSelector } from 'store/state/selectors/savedSearchPage';
import { pushNotificationsFeatureEnabled } from 'utils/pushNotificationsFeatureEnabled';
import { isSearchEqual } from 'utils/saveSearch';
import { prepareSearchParamsWithSecureAmenities } from './routing/handlers/search/utils';


const TOO_GENERAL_DOC_IDS = [ 'ישראל' ];

function getIsTooGeneralLocation(locationDocIds?: string[]) {
  if (!locationDocIds || locationDocIds.length !== 1) return false;
  return TOO_GENERAL_DOC_IDS.includes(locationDocIds[0]);
}

function* saveSearchWithValidationWorker(action: ReturnType<typeof saveSearchWithValidation>) {
  const { payload: search } = action;
  const { meta } = action;

  const pendingAction: IPendingSaveSearch = {
    type: PendingActionType.SaveSearch,
    meta,
    ...search,
  };

  const authGateResponse: AuthGateResponse = yield call(authGate, pendingAction);

  if (authGateResponse === AuthGateResponse.Rejected) {
    return;
  }

  const isTooGeneralLocation = getIsTooGeneralLocation(search.locationDocIds);
  if (isTooGeneralLocation) {
    yield put(setSavedSearchActiveModal(SavedSearchActiveModal.EnhanceSearch));
    return;
  }

  const previousSavedSearchId = yield select(previousSavedSearchIdSelector);
  if (previousSavedSearchId) {
    yield put(setSavedSearchActiveModal(SavedSearchActiveModal.ExistingSearch));
    return;
  }

  const savedSearches: ISaveSearch[] = yield select(savedSearchesListSelector);

  const { amenities, restParams, secureRoomType } = prepareSearchParamsWithSecureAmenities(search.query);
  const secureSearch = search.query ? {
    ...search,
    query: {
      ...search.query,
      ...restParams,
      amenities,
      secureRoomType,
    },
  } : search;

  const hasSaved = savedSearches.find((s) => isSearchEqual(s, secureSearch)) !== undefined;

  if (!hasSaved) {
    yield put(mutate({
      mutationType: MutationType.SaveSearchQuery,
      meta: {
        variables: {
          search: secureSearch,
        },
      },
    }));
  }

  yield put(setSavedSearchActiveModal(SavedSearchActiveModal.PostSaved));
  yield put(setMenuDotVisible(true));
}

function* saveSearchWorker(action: ReturnType<typeof saveSearch>) {
  const { payload: search } = action;
  const { meta } = action;

  const pendingAction: IPendingSaveSearch = {
    type: PendingActionType.SaveSearch,
    meta,
    ...search,
  };

  const authGateResponse: AuthGateResponse = yield call(authGate, pendingAction);

  if (authGateResponse === AuthGateResponse.Rejected) {
    return;
  }

  const savedSearches: ISaveSearch[] = yield select(savedSearchesListSelector);
  const { amenities, restParams, secureRoomType } = prepareSearchParamsWithSecureAmenities(search.query);
  const secureSearch = search.query ? {
    ...search,
    query: {
      ...search.query,
      ...restParams,
      amenities,
      secureRoomType,
    },
  } : search;

  const hasSaved = savedSearches.find((s) => isSearchEqual(s, secureSearch)) !== undefined;

  if (!meta.saveSilently) {
    const shouldUsePushFlow = yield call(pushNotificationsFeatureEnabled);

    if (shouldUsePushFlow) {
      yield put(navigateTo(Route.SavedSearchesPage));
    }
    else {
      yield put(setToastMessage({ term: 'savedSearch.addMessage', params: { frequency: search.updateFrequency } }));
    }

    if (hasSaved) {
      yield put(setToastMessage({ term: 'savedSearch.duplicateMessage' }));
    }
  }

  if (!hasSaved) {
    yield put(mutate({
      mutationType: MutationType.SaveSearchQuery,
      meta: {
        variables: {
          search: secureSearch,
        },
      },
    }));
  }

  yield put(setMenuDotVisible(true));
}

function* updateSearchFrequencyWorker(action: ReturnType<typeof updateSaveSearchFrequency>) {
  const { payload: search } = action;

  const isAuthenticated = yield call(authGate);
  if (isAuthenticated === AuthGateResponse.Rejected) return;

  yield put(setNotificationBannerMessage({
    typeId: BannerType.FrequencyUpdated,
    timeout: 5000,
  }));

  const { amenities, restParams, secureRoomType } = prepareSearchParamsWithSecureAmenities(search.query);

  yield put(mutate({
    mutationType: MutationType.UpdateSearchQuery,
    meta: {
      variables: {
        search: search.query ? {
          ...search,
          query: {
            ...search.query,
            ...restParams,
            amenities,
            secureRoomType,
          },
        } : search,
      },
    },
  }));
}

function* updateSearchWorker(action: ReturnType<typeof updateSaveSearch>) {
  const { payload: search } = action;

  const isAuthenticated = yield call(authGate);
  if (isAuthenticated === AuthGateResponse.Rejected) return;

  const { amenities, restParams, secureRoomType } = prepareSearchParamsWithSecureAmenities(search.query);

  yield put(mutate({
    mutationType: MutationType.UpdateSearchQuery,
    meta: {
      variables: {
        search: search.query ? {
          ...search,
          query: {
            ...search.query,
            ...restParams,
            amenities,
            secureRoomType,
          },
        } : search,
      },
    },
  }));
}

function* deleteSaveSearchWorker(action: ReturnType<typeof deleteSaveSearch>) {
  const isAuthenticated = yield call(authGate);
  if (isAuthenticated === AuthGateResponse.Rejected) return;

  const searchId = action.payload;

  if (searchId) {
    yield put(mutate({
      mutationType: MutationType.DeleteSaveSearch,
      meta: {
        variables: { searchId },
      },
    }));
  }

}

export function* saveSearchWatcher() {
  yield takeEvery(SAVE_SEARCH, saveSearchWorker);
  yield takeEvery(SAVE_SEARCH_WITH_VALIDATION, saveSearchWithValidationWorker);
  yield takeEvery(UPDATE_SAVED_SEARCH, updateSearchWorker);
  yield takeEvery(UPDATE_SAVED_SEARCH_FREQUENCY, updateSearchFrequencyWorker);
  yield takeEvery(DELETE_SAVE_SEARCH, deleteSaveSearchWorker);
}
